// Flow consumes retained producer evidence. Every request in this module is a GET.
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { marketDashboard, decodeSessionCalendarHistory } from './mackFlowCore';
import { apiBase, authToken } from '../api/apiBase';
import { getRexySocket } from '../hooks/useRexySocket';
import { subscribeMackInputsChanges } from './mackInputsNotifications';
import { subscribeMackCalculatorChanges } from './mackCalculatorNotifications';
import { subscribeMackFlowChanges } from './mackFlowNotifications';
import { decodePermanentPriceHistory, PermanentPriceHistory } from './mackRetainedPriceHistory';

export const FLOW_READ_TIMEOUT_MS = 30_000;
export const FLOW_MARKETS = ['GBP/JPY', 'USD/JPY', 'EUR/USD', 'GBP/USD', 'AUD/JPY', 'EUR/GBP', 'XAU/USD', 'Brent', 'WTI', 'Nasdaq 100', 'S&P 500', 'Dow', 'DAX', 'FTSE 100'] as const;
export interface FlowEvidence {
  field_id: string; producer: string; label: string; state: string; current_eligible: boolean;
  value: unknown; unit: string | null; frequency?: string | null; series_id: string | Record<string, unknown> | null;
  publisher: string | null; source_url: string | null; reference_period: string | null;
  publication_at?: string | null; updated_at?: string | null; observed_at?: string | null;
  retrieved_at?: string | null; recorded_at?: string | null; reason?: string | null; reason_code?: string | null;
  history: { count: number; distinct_periods?: number; first_period?: string | null; last_period?: string | null; url?: string };
  consumers: string[]; [key: string]: unknown;
  permanent_price_history?: PermanentPriceHistory;
}
export interface FlowMarket {
  market: string; market_id: string; price: FlowEvidence; turnover_components: FlowEvidence[];
  outputs: Record<string, null>; definition_gaps: unknown[];
  session_context?: { consumer_id: string; calendar_dependencies?: unknown };
}
export interface FlowProjection {
  success: true; schema: string; generated_at: string; state: string; formula_state: 'FORMULA_PASS_2_PENDING';
  coverage: { field_occurrences: number; consumer_templates: number; address_keys: number; contracts: number; [key: string]: unknown };
  population: { markets: string[]; sheets: string[]; participants: string[]; dealers: unknown; vix_support_only: boolean };
  markets: FlowMarket[]; supporting: { vix: FlowEvidence; [key: string]: unknown };
  panels: { id: string; label: string; formula_state: string; outputs: Record<string, null>; definition_gaps: unknown[]; [key: string]: unknown }[];
  withholds: FlowEvidence[];
  definition_gaps?: unknown[];
  refresh: { inputs_signal: string | null; calculator_signal: string | null; phases_signal: string | null; reconnect: boolean; manual: boolean };
  authority: Record<string, unknown>; [key: string]: unknown;
  retained_price_views?: { market_id: string | null; field_id: string; consumer_id: string; meaning: string; evidence: FlowEvidence }[];
  source_information?: {
    schema: string; kind: string; formula_execution: false; calendar_observations_admitted: 0;
    source_fetches: 0; writer_entries: 0; participant_presence: null; L: null; WindowEffect: null; runtime_schedule: null;
    contexts: { market_id: string; consumer_id: string; price_identity: string; provider: string;
      series: string; context_kind: string; reference_timezone: string | null; blocking_reasons: string[];
      current_eligible: false; L: null; WindowEffect: null; participant_presence: null }[];
    windows: { window: string; market: string; iana_timezone: string; start_inclusive_local: string;
      end_exclusive_local: string; start_utc: string; end_exclusive_utc: string; consumers: string[];
      boundary_semantics: 'START_INCLUSIVE_END_EXCLUSIVE'; production_admissible: false;
      L: null; WindowEffect: null; participant_presence: null; runtime_schedule: null }[];
    source_definition: { path: string; sha256: string; recorded_at_utc: string };
  };
}
export interface FlowPage {
  success: true; rows: Record<string, unknown>[]; total: number; offset: number; next_offset: number | null;
  field_id?: string; producer?: string; [key: string]: unknown;
}
export type FlowReadErrorKind = 'unauthenticated' | 'permission' | 'timeout' | 'transport' | 'response' | 'cancelled';
export class FlowReadError extends Error {
  constructor(public kind: FlowReadErrorKind, message: string) { super(message); this.name = 'FlowReadError'; }
}
const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const whole = (value: unknown): value is number => typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
const uniqueStrings = (value: unknown): value is string[] => Array.isArray(value) && value.every(v => typeof v === 'string' && v.length > 0) && new Set(value).size === value.length;
function evidence(value: unknown): value is FlowEvidence {
  return object(value) && typeof value.field_id === 'string' && typeof value.producer === 'string' &&
    typeof value.state === 'string' && typeof value.current_eligible === 'boolean' && object(value.history) &&
    whole(value.history.count) && Array.isArray(value.consumers) &&
    !(typeof value.value === 'number' && !Number.isFinite(value.value));
}
export function decodeFlowProjection(raw: unknown): FlowProjection {
  const p = raw as FlowProjection;
  if (!p || p.success !== true || typeof p.schema !== 'string' || p.formula_state !== 'FORMULA_PASS_2_PENDING' ||
      !p.coverage || p.coverage.field_occurrences !== 1880 || p.coverage.consumer_templates !== 99 ||
      p.coverage.address_keys !== 840 || p.coverage.contracts !== 113 || !p.population ||
      !uniqueStrings(p.population.sheets) || p.population.sheets.length !== 6 || !uniqueStrings(p.population.participants) ||
      p.population.participants.length !== 10 || p.population.vix_support_only !== true ||
      !Array.isArray(p.markets) || p.markets.length !== 14 || !Array.isArray(p.panels) || p.panels.length !== 4 ||
      !Array.isArray(p.withholds) || !p.withholds.every(evidence) || !p.supporting || !evidence(p.supporting.vix)) {
    throw new FlowReadError('response', 'The Flow retained-information response did not match its declared field and population contract.');
  }
  const markets = new Set<string>(FLOW_MARKETS);
  for (const row of p.markets) {
    if (!markets.delete(row.market) || !evidence(row.price) || !Array.isArray(row.turnover_components) ||
        !row.turnover_components.every(evidence) || !object(row.outputs) || Object.values(row.outputs).some(value => value !== null)) {
      throw new FlowReadError('response', 'Flow market evidence or pending calculation cells were invalid.');
    }
  }
  const panels = new Set(['capacity', 'contracts', 'liquidity', 'rotation']);
  for (const panel of p.panels) {
    if (!panels.delete(panel.id) || panel.formula_state !== 'FORMULA_PASS_2_PENDING' ||
        !object(panel.outputs) || Object.values(panel.outputs).some(value => value !== null)) {
      throw new FlowReadError('response', 'Flow formula outputs must remain pending.');
    }
  }
  const info = p.source_information;
  if (info !== undefined && (!object(info) || info.schema !== 'mack_flow_approved_definition_information_v1' ||
      info.kind !== 'OWNER_APPROVED_DEFINITIONS_NOT_CALENDAR_OBSERVATIONS' || info.formula_execution !== false ||
      info.calendar_observations_admitted !== 0 || info.source_fetches !== 0 || info.writer_entries !== 0 ||
      ['L', 'WindowEffect', 'participant_presence', 'runtime_schedule'].some(k => (info as unknown as Record<string, unknown>)[k] !== null) ||
      !object(info.source_definition) || typeof info.source_definition.path !== 'string' ||
      !/^[a-f0-9]{64}$/.test(info.source_definition.sha256) ||
      !Array.isArray(info.contexts) || info.contexts.length !== 14 || info.contexts.some(r => !object(r)) || new Set(info.contexts.map(r => r.market_id)).size !== 14 ||
      info.contexts.some(r => !object(r) || !p.markets.some(m => m.market_id === r.market_id && m.price.field_id === r.price_identity) ||
        r.consumer_id !== `flow.market.${r.market_id}.market_session_state` || typeof r.provider !== 'string' || typeof r.series !== 'string' ||
        typeof r.context_kind !== 'string' || !(r.reference_timezone === null || typeof r.reference_timezone === 'string') ||
        r.current_eligible !== false || (['L', 'WindowEffect', 'participant_presence'] as const).some(k => r[k] !== null) ||
        !Array.isArray(r.blocking_reasons) || !r.blocking_reasons.every(v => typeof v === 'string')) ||
      !Array.isArray(info.windows) || info.windows.length !== 32 || info.windows.some(r => !object(r)) || new Set(info.windows.map(r => `${r.window}:${r.market}`)).size !== 32 ||
      new Set(info.windows.map(r => r.window)).size !== 5 ||
      info.windows.some(r => !object(r) || r.boundary_semantics !== 'START_INCLUSIVE_END_EXCLUSIVE' || r.production_admissible !== false ||
        (['L', 'WindowEffect', 'participant_presence', 'runtime_schedule'] as const).some(k => r[k] !== null) ||
        !['window', 'market', 'iana_timezone', 'start_inclusive_local', 'end_exclusive_local', 'start_utc', 'end_exclusive_utc']
        .every(k => typeof (r as unknown as Record<string, unknown>)[k] === 'string')))) {
    throw new FlowReadError('response', 'Approved definition information was refused; it cannot supply calendar observations or effects.');
  }
  if (p.retained_price_views !== undefined) {
    const expected = new Map([
      ['mack.shared.market.tradingview.oanda_xauusd.1m', ['xau_usd', 'flow.market.xau_usd.shared_market_observation']],
      ['mack.shared.market.tradingview.ig_nasdaq.profile_60.price', ['nasdaq_100', 'flow.market.nasdaq_100.shared_hourly_range_price']],
      ['mack.shared.market.tradingview.capitalcom_vix_cfd.chart_close', [null, 'flow.volatility.capitalcom_vix_cfd.supporting_market_observation']],
    ]);
    if (!Array.isArray(p.retained_price_views) || p.retained_price_views.length !== 3 ||
        p.retained_price_views.some(row => {
          if (!object(row)) return true;
          const binding = expected.get(row.field_id); expected.delete(row.field_id);
          return !binding || row.market_id !== binding[0] || row.consumer_id !== binding[1] || typeof row.meaning !== 'string' ||
            !evidence(row.evidence) || row.evidence.field_id !== row.field_id || !row.evidence.consumers.includes(row.consumer_id);
        })) throw new FlowReadError('response', 'Retained price evidence or its separate consumer identity was refused.');
  }
  try { return marketDashboard(p); }
  catch (error) { throw new FlowReadError('response', error instanceof Error ? error.message : 'Flow retained bindings were refused.'); }
}
function decodePage(raw: unknown, offset: number): FlowPage {
  const page = raw as FlowPage;
  if (!page || page.success !== true || !Array.isArray(page.rows) || page.rows.length > 50 ||
      !whole(page.total) || page.offset !== offset || (page.next_offset !== null && (!whole(page.next_offset) || page.next_offset <= offset))) {
    throw new FlowReadError('response', 'The selected retained-information page was invalid.');
  }
  return page;
}
export function flowReadError(error: unknown): FlowReadError {
  if (error instanceof FlowReadError) return error;
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 401) return new FlowReadError('unauthenticated', 'This application session is unauthenticated or has expired. Use the normal owner session.');
    if (error.response?.status === 403) return new FlowReadError('permission', 'This authenticated session does not have access to Flow retained information.');
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') return new FlowReadError('timeout', 'The retained-information read timed out. No acquisition was requested.');
    if (!error.response) return new FlowReadError('transport', 'No backend response was received. Previously confirmed information is preserved.');
    return new FlowReadError('response', `The retained-information request failed (HTTP ${error.response.status}). No acquisition was requested.`);
  }
  return new FlowReadError('response', error instanceof Error ? error.message : 'The retained-information response could not be read.');
}
async function get(path: string, params: Record<string, unknown>, token?: string | null, signal?: AbortSignal): Promise<unknown> {
  const controller = new AbortController();
  const cancel = () => controller.abort();
  if (signal?.aborted) throw new FlowReadError('cancelled', 'Read cancelled.');
  signal?.addEventListener('abort', cancel, { once: true });
  let timer: ReturnType<typeof setTimeout> | undefined;
  let abortListener: (() => void) | undefined;
  try {
    const deadline = new Promise<never>((_, reject) => {
      timer = setTimeout(() => { reject(new FlowReadError('timeout', 'The retained-information read timed out. No acquisition was requested.')); controller.abort(); }, FLOW_READ_TIMEOUT_MS);
      abortListener = () => reject(new FlowReadError('cancelled', 'Read cancelled.'));
      signal?.addEventListener('abort', abortListener, { once: true });
    });
    const request = axios.get(`${apiBase()}/mack/flow${path}`, {
      params, signal: controller.signal, timeout: FLOW_READ_TIMEOUT_MS, withCredentials: true,
      headers: { Authorization: `Bearer ${token || authToken()}`, 'Cache-Control': 'no-cache' },
    });
    const response = await Promise.race([request, deadline]);
    return response.data;
  } catch (error) { throw flowReadError(error); }
  finally { if (timer) clearTimeout(timer); signal?.removeEventListener('abort', cancel); if (abortListener) signal?.removeEventListener('abort', abortListener); }
}
export async function fetchFlowInformation(token?: string | null, signal?: AbortSignal): Promise<FlowProjection> {
  return decodeFlowProjection(await get('', { view: 'dashboard' }, token, signal));
}
export async function fetchFlowEvidence(fieldId: string, token?: string | null, signal?: AbortSignal): Promise<FlowEvidence> {
  const raw = await get('/evidence', { field_id: fieldId }, token, signal);
  if (!evidence(raw) || raw.field_id !== fieldId) throw new FlowReadError('response', 'Selected Flow evidence identity did not match.');
  if (raw.permanent_price_history !== undefined) {
    try { raw.permanent_price_history = decodePermanentPriceHistory(raw.permanent_price_history, fieldId, 0); }
    catch (error) { throw new FlowReadError('response', error instanceof Error ? error.message : 'Permanent history was refused.'); }
  }
  return raw;
}
export async function fetchFlowHistory(fieldId: string, offset = 0, token?: string | null, signal?: AbortSignal): Promise<FlowPage> {
  const page = decodePage(await get('/history', { field_id: fieldId, offset, limit: 30 }, token, signal), offset);
  if (page.field_id !== fieldId) throw new FlowReadError('response', 'Selected history identity did not match.');
  return page;
}
export interface SessionCalendarPage extends FlowPage {
  governed_observation_count: null;
  calendar_dependencies: { consumer_id: string; retention_binding_state: 'UNBOUND_IN_SELECTED_CALENDAR_AND_FLOW_T1' | 'INSTALLED_BOE_DOCUMENT_RETENTION_BINDING'; required_originals: {
    request_id: string; publisher: string; dataset: string; canonical_source_urls: string[];
  }[] };
}
export async function fetchFlowSessionCalendarHistory(consumer: string, token?: string | null, signal?: AbortSignal): Promise<SessionCalendarPage> {
  const raw = await get('/history', { field_id: consumer, offset: 0, limit: 30 }, token, signal);
  try { return decodeSessionCalendarHistory(raw, consumer, 0) as SessionCalendarPage; }
  catch (error) { throw new FlowReadError('response', error instanceof Error ? error.message : 'Calendar history was refused.'); }
}
export async function fetchFlowPermanentPriceHistory(fieldId: string, offset = 0, token?: string | null, signal?: AbortSignal): Promise<PermanentPriceHistory> {
  const page = await fetchFlowHistory(fieldId, offset, token, signal);
  try { return decodePermanentPriceHistory(page.permanent_price_history, fieldId, offset); }
  catch (error) { throw new FlowReadError('response', error instanceof Error ? error.message : 'Permanent history was refused.'); }
}
export async function fetchFlowRegistry(kind: string, offset = 0, filters: Record<string, string> = {}, token?: string | null, signal?: AbortSignal): Promise<FlowPage> {
  const path = kind === 'sources' ? '/sources' : kind === 'addresses' ? '/addresses' : '/fields';
  return decodePage(await get(path, { ...filters, ...(path === '/fields' ? { kind } : {}), offset, limit: 30 }, token, signal), offset);
}
export function useFlowInformation(suppliedToken?: string | null) {
  const token = suppliedToken || authToken();
  const [projection, setProjection] = useState<FlowProjection | null>(null);
  const [error, setError] = useState<FlowReadError | null>(null);
  const [loading, setLoading] = useState(false);
  const active = useRef<AbortController | null>(null);
  const reload = useCallback(async () => {
    active.current?.abort();
    const controller = new AbortController(); active.current = controller;
    setLoading(true); setError(null);
    try {
      const next = await fetchFlowInformation(token, controller.signal);
      if (active.current === controller && !controller.signal.aborted) setProjection(next);
    } catch (failure) {
      if (active.current === controller && !controller.signal.aborted) setError(flowReadError(failure));
    } finally { if (active.current === controller) setLoading(false); }
  }, [token]);
  useEffect(() => {
    setProjection(null); setError(null); void reload();
    const socket = getRexySocket();
    const stop = subscribeMackInputsChanges(socket, () => { void reload(); });
    // Inputs already owns the one reconnect read for this consumer.
    const stopCalculator = subscribeMackCalculatorChanges(socket, () => { void reload(); }, { reconnect: false });
    const stopMarket = subscribeMackFlowChanges(socket, () => { void reload(); });
    return () => { active.current?.abort(); active.current = null; stop(); stopCalculator(); stopMarket(); };
  }, [token, reload]);
  return { projection, error, loading, reload };
}
